import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MagnifyingGlass, AirplaneTilt, Star, CalendarBlank, Package as PackageIcon } from '@phosphor-icons/react';
import Navigation from '@/components/Navigation';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import LocationPicker from '@/components/LocationPicker';

const SearchTravelers = () => {
  const [trips, setTrips] = useState([]);
  const [filteredTrips, setFilteredTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [origin, setOrigin] = useState({ country: '', city: '' });
  const [destination, setDestination] = useState({ country: '', city: '' });
  const [date, setDate] = useState('');

  useEffect(() => {
    let cancelled = false;
    // Read hero-search URL params (origin/destination/date) so the picker flows in
    const params = new URLSearchParams(window.location.search);
    const o = { country: params.get('origin_country') || '', city: params.get('origin_city') || '' };
    const d = { country: params.get('destination_country') || '', city: params.get('destination_city') || '' };
    const date0 = params.get('date') || '';
    Promise.resolve().then(() => {
      if (cancelled) return;
      if (o.country) setOrigin(o);
      if (d.country) setDestination(d);
      if (date0) setDate(date0);
    });
    const fetchTrips = async () => {
      try {
        const qs = new URLSearchParams({ status: 'active' });
        if (date0) qs.set('date', date0);
        const response = await fetch(`${API}/trips?${qs}`, { credentials: 'include' });
        const data = await response.json();
        if (cancelled) return;
        setTrips(data);
        setFilteredTrips(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching trips:', error);
        if (!cancelled) setLoading(false);
      }
    };

    fetchTrips();
    return () => { cancelled = true; };
  }, []);

  const handleSearch = () => {
    let filtered = trips;

    if (origin.country) {
      filtered = filtered.filter((trip) => trip.departure_country === origin.country);
    }
    if (origin.city) {
      filtered = filtered.filter((trip) => trip.departure_city === origin.city);
    }
    if (destination.country) {
      filtered = filtered.filter((trip) => trip.destination_country === destination.country);
    }
    if (destination.city) {
      filtered = filtered.filter((trip) => trip.destination_city === destination.city);
    }
    if (date) {
      // Trip's departure_date must be on/after the user's needed date
      filtered = filtered.filter((trip) => (trip.departure_date || '') >= date);
    }

    setFilteredTrips(filtered);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9FAFB]">
        <Navigation />
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6D28D9]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      <Navigation />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1
          className="text-4xl font-extrabold text-gray-900 mb-8"
          style={{ fontFamily: 'Outfit, sans-serif' }}
          data-testid="search-title"
        >
          Find Travelers
        </h1>

        <div className="bg-white p-6 rounded-xl border border-gray-200 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Origin</label>
              <LocationPicker value={origin} onChange={setOrigin} mode="flat" testIdPrefix="search-origin" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">Destination</label>
              <LocationPicker value={destination} onChange={setDestination} mode="flat" testIdPrefix="search-destination" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-2">On/After Date</label>
              <div className="relative">
                <CalendarBlank size={18} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="date"
                  value={date}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setDate(e.target.value)}
                  className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#6D28D9] focus:ring-2 focus:ring-purple-100"
                  data-testid="search-date"
                />
              </div>
            </div>
          </div>
          <Button
            onClick={handleSearch}
            className="bg-[#6D28D9] hover:bg-[#5B21B6] text-white rounded-lg flex items-center justify-center gap-2 w-full md:w-auto px-8"
            data-testid="search-button"
          >
            <MagnifyingGlass size={20} weight="bold" />
            Search
          </Button>
        </div>

        {filteredTrips.length === 0 ? (
          <div className="p-12 rounded-xl border border-gray-200 bg-white text-center" data-testid="no-results">
            <AirplaneTilt size={48} weight="duotone" className="text-gray-500 mx-auto mb-4" />
            <p className="text-gray-600">No travelers found matching your criteria</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTrips.map((trip) => (
              <Link
                key={trip.trip_id}
                to={`/trips/${trip.trip_id}`}
                className="p-6 rounded-xl border border-gray-200 bg-white hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                data-testid={`trip-result-${trip.trip_id}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  {trip.traveler_image ? (
                    <img
                      src={trip.traveler_image}
                      alt={trip.traveler_name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-[#1E2A45] flex items-center justify-center">
                      <AirplaneTilt size={24} weight="duotone" className="text-gray-600" />
                    </div>
                  )}
                  <div className="flex-1">
                    <p className="font-bold text-gray-900">{trip.traveler_name}</p>
                    {trip.traveler_rating > 0 && (
                      <div className="flex items-center gap-1">
                        <Star size={16} weight="fill" className="text-[#F59E0B]" />
                        <span className="text-sm text-gray-600">{trip.traveler_rating.toFixed(1)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-3 mb-4">
                  <div className="flex items-start gap-2">
                    <AirplaneTilt size={20} weight="bold" className="text-[#6D28D9] flex-shrink-0 mt-1" />
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">Route</p>
                      <p className="font-bold text-gray-900">
                        {trip.departure_city}, {trip.departure_country}
                      </p>
                      <p className="text-gray-600">↓</p>
                      <p className="font-bold text-gray-900">
                        {trip.destination_city}, {trip.destination_country}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <CalendarBlank size={20} weight="bold" className="text-[#6D28D9]" />
                    <div>
                      <p className="text-sm text-gray-500">Departure</p>
                      <p className="font-medium text-gray-900">
                        {new Date(trip.departure_date).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <PackageIcon size={20} weight="bold" className="text-[#6D28D9]" />
                    <div>
                      <p className="text-sm text-gray-500">Available</p>
                      <p className="font-medium text-gray-900">{trip.available_weight} kg</p>
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200 flex items-center justify-between">
                  <span className="text-sm text-gray-500">Price per kg</span>
                  <span className="text-2xl font-bold text-[#6D28D9]">${trip.price_per_kg}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchTravelers;
